import get from 'lodash/get';
import Config from 'react-native-config';
import { Dispatch } from 'redux';
import { CostProvider, GuidedSearchTopic } from 'store/cost/types';
import * as costSelectors from 'store/cost/selectors';
import { selectHealthFacetsInsuranceSelector } from 'store/profile/selectors';
import { Insurance } from 'store/profile/types';
import {
  AdapterSource,
  Coordinate,
  DispatcherAuthPrefix,
  DispatcherCategoryService,
  DispatcherResource,
  DispatcherEndpoint
} from 'modules/types/common';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import { DFDHeaders } from 'serverTypes/authentication';
import { RootState } from 'store/types';
import { costTermsURL } from './constants';
import * as types from './types';

export const UPDATE_SCREEN_ITEMS = 'COST.UPDATE_SCREEN_ITEMS';

export const GET_PROVIDERS = 'COST.GET_PROVIDERS';
export const GET_PROVIDERS_SUCCESS = 'COST.GET_PROVIDERS_SUCCESS';
export const GET_PROVIDERS_FAIL = 'COST.GET_PROVIDERS_FAIL';

export const GET_PROVIDERS_PAGINATED = 'COST.GET_PROVIDERS_PAGINATED';
export const GET_PROVIDERS_PAGINATED_SUCCESS = 'COST.GET_PROVIDERS_PAGINATED_SUCCESS';
export const GET_PROVIDERS_PAGINATED_FAIL = 'COST.GET_PROVIDERS_PAGINATED_FAIL';

export const SET_PROVIDER_FOR_BOOKING = 'COST.SET_PROVIDER_FOR_BOOKING';
export const CLEAR_PROVIDER_FOR_BOOKING = 'COST.CLEAR_PROVIDER_FOR_BOOKING';

export const GET_PROVIDER_COST = 'COST.GET_PROVIDER_COST';
export const GET_PROVIDER_COST_SUCCESS = 'COST.GET_PROVIDER_COST_SUCCESS';
export const GET_PROVIDER_COST_FAIL = 'COST.GET_PROVIDER_COST_FAIL';

export const PROVIDER_GET_DETAILS_FOR_COST = 'COST.PROVIDER_GET_DETAILS_FOR_COST';
export const PROVIDER_GET_DETAILS_FOR_COST_SUCCESS = 'COST.PROVIDER_GET_DETAILS_FOR_COST_SUCCESS';
export const PROVIDER_GET_DETAILS_FOR_COST_FAIL = 'COST.PROVIDER_GET_DETAILS_FOR_COST_FAIL';

export const GET_PATIENT_COST = 'COST.GET_PATIENT_COST';
export const GET_PATIENT_COST_SUCCESS = 'COST.GET_PATIENT_COST_SUCCESS';
export const GET_PATIENT_COST_FAIL = 'COST.GET_PATIENT_COST_FAIL';
export const SET_COST_ESTIMATOR_SEARCH_TEXT = 'COST.SET_COST_ESTIMATOR_SEARCH_TEXT';

export const GET_VISIT_TYPE_ID = 'COST.GET_VISIT_TYPE_ID';
export const GET_VISIT_TYPE_ID_SUCCESS = 'COST.GET_VISIT_TYPE_ID_SUCCESS';
export const GET_VISIT_TYPE_ID_FAIL = 'COST.GET_VISIT_TYPE_ID_FAIL';

export const GET_TERMS_AND_CONDITIONS = 'COST.GET_TERMS_AND_CONDITIONS';
export const GET_TERMS_AND_CONDITIONS_SUCCESS = 'COST.GET_TERMS_AND_CONDITIONS_SUCCESS';
export const GET_TERMS_AND_CONDITIONS_FAIL = 'COST.GET_TERMS_AND_CONDITIONS_FAIL';
export const SET_ACCEPT_TC = 'COST.SET_ACCEPT_TC';

export const GET_SEARCH_RESULTS = 'COST.GET_SEARCH_RESULTS';
export const GET_SEARCH_RESULTS_SUCCESS = 'COST.GET_SEARCH_RESULTS_SUCCESS';
export const GET_SEARCH_RESULTS_FAIL = 'COST.GET_SEARCH_RESULTS_FAIL';

export const getSearchResults = (text: string, coords: Coordinate) => {
  if (!!Config.GET_CARE_SERVICE_EXPANSION_ENABLED) {
    return {
      type: GET_SEARCH_RESULTS,
      payload: {
        client: 'dispatcher',
        request: {
          method: 'get',
          url: getDispatcherUrl(
            DispatcherCategoryService.GET_CARE,
            DispatcherAuthPrefix.AUTHENTICATED,
            DispatcherResource.SERVICE_GROUP,
            DispatcherEndpoint.SEARCH
          ),
          headers: {
            [DFDHeaders.mhpAdapter]: AdapterSource.MHP
          },
          params: {
            latitude: coords.latitude,
            longitude: coords.longitude,
            query: text,
            page: 0,
            distance: 500
          }
        }
      }
    };
  }
  return {
    type: GET_SEARCH_RESULTS,
    payload: {
      client: 'cost',
      request: {
        method: 'get',
        url: `${Config.COST_URL_GET_TEXT_SEARCH}`,
        params: {
          latitude: coords.latitude,
          longitude: coords.longitude,
          query: text,
          page: 0,
          distance: 500
        }
      }
    }
  };
};

export const SELECT_SEARCH_RESULT = 'COST.SELECT_SEARCH_RESULT';
export const selectSearchResult = (item: GuidedSearchTopic) => ({
  type: SELECT_SEARCH_RESULT,
  payload: item
});

export const SELECT_PROVIDER = 'COST.SELECT_PROVIDER';
export const selectProvider = (provider: CostProvider) => ({
  type: SELECT_PROVIDER,
  payload: provider
});

export const GET_PLAN_BENEFITS = 'COST.GET_PLAN_BENEFITS';
export const GET_PLAN_BENEFITS_SUCCESS = 'COST.GET_PLAN_BENEFITS_SUCCESS';
export const GET_PLAN_BENEFITS_FAIL = 'COST.GET_PLAN_BENEFITS_FAIL';

export const getPlanBenefits = () => async (dispatch: Dispatch, getState: () => RootState) => {
  if (!!Config.GET_CARE_SERVICE_EXPANSION_ENABLED) {
    const state = getState();
    const insurance = selectHealthFacetsInsuranceSelector(state) as Insurance;
    dispatch({
      type: GET_PLAN_BENEFITS,
      payload: {
        client: 'dispatcher',
        request: {
          method: 'get',
          url: getDispatcherUrl(
            DispatcherCategoryService.GET_CARE,
            DispatcherAuthPrefix.AUTHENTICATED,
            DispatcherResource.PLAN_BENEFITS,
            DispatcherEndpoint.SELECT_HEALTH
          ),
          headers: {
            [DFDHeaders.mhpAdapter]: AdapterSource.MHP,
            [DFDHeaders.resourceId]: insurance.planId,
            [DFDHeaders.mhpFilterSubscriberId]: insurance.subscriberId
          }
        }
      }
    });
    return;
  }
  dispatch({
    type: GET_PLAN_BENEFITS,
    payload: {
      client: 'cost',
      request: {
        method: 'get',
        url: Config.COST_URL_GET_PLAN_BENEFITS
      }
    }
  });
};

export const GET_GUIDED_SEARCH = 'COST.GET_GUIDED_SEARCH';
export const GET_GUIDED_SEARCH_SUCCESS = 'COST.GET_GUIDED_SEARCH_SUCCESS';
export const GET_GUIDED_SEARCH_FAIL = 'COST.GET_GUIDED_SEARCH_FAIL';

export const getGuidedSearch = () => {
  if (!!Config.GET_CARE_SERVICE_EXPANSION_ENABLED) {
    return {
      type: GET_GUIDED_SEARCH,
      payload: {
        client: 'dispatcher',
        request: {
          method: 'get',
          url: getDispatcherUrl(
            DispatcherCategoryService.GET_CARE,
            DispatcherAuthPrefix.AUTHENTICATED,
            DispatcherResource.SERVICE_GROUP
          ),
          headers: {
            [DFDHeaders.mhpAdapter]: AdapterSource.MHP
          }
        }
      }
    };
  }
  return {
    type: GET_GUIDED_SEARCH,
    payload: {
      client: 'cost',
      request: {
        method: 'get',
        url: Config.COST_URL_GET_GUIDED_SEARCH
      }
    }
  };
};

export const updateScreenItems = (history: Record<string, any>) => ({
  type: UPDATE_SCREEN_ITEMS,
  payload: history
});

export const getProviderCost = (medcostid: string) => ({
  type: GET_PROVIDER_COST,
  payload: {
    client: 'cost',
    request: {
      method: 'get',
      url: `${Config.COST_URL_GET_PROVIDER_COST}`,
      params: {
        medcostid
      }
    }
  }
});

export const clearProviderForBooking = () => ({
  type: CLEAR_PROVIDER_FOR_BOOKING
});

export const getVisitTypeID = (id: string) => {
  if (!!Config.GET_CARE_SERVICE_EXPANSION_ENABLED) {
    return {
      type: GET_VISIT_TYPE_ID,
      payload: {
        client: 'dispatcher',
        request: {
          method: 'get',
          url: getDispatcherUrl(
            DispatcherCategoryService.GET_CARE,
            DispatcherAuthPrefix.AUTHENTICATED,
            DispatcherResource.VISIT_TYPES
          ),
          params: {
            servicegroupid: id
          },
          headers: {
            [DFDHeaders.mhpAdapter]: AdapterSource.MHP,
            [DFDHeaders.resourceId]: id
          }
        }
      }
    };
  }
  return {
    type: GET_VISIT_TYPE_ID,
    payload: {
      client: 'cost',
      request: {
        method: 'get',
        url: `${Config.COST_GET_VISIT_TYPE}/?`,
        params: {
          servicegroupid: id
        }
      }
    }
  };
};

export const getServiceGroupIdByVisitTypeId = (id: string) => ({
  type: 'GET_SERVICE_GROUP_ID',
  payload: {
    client: 'cost',
    request: {
      method: 'get',
      url: '/services/cost-estimator-service/estimatecost/service-group-id',
      params: {
        visittypeid: id
      }
    }
  }
});

export const setupGuidedSearch = () => async (dispatch: Function, getState: Function) => {
  await dispatch(getGuidedSearch());
  const guidedSearchTopics = costSelectors.guidedSearchTopicsSelector(getState());
  await dispatch(
    updateScreenItems({
      history: [guidedSearchTopics],
      level: 0
    })
  );
};

export const setAcceptCostTC = () => ({
  type: SET_ACCEPT_TC
});

export const getCostTermsAndConditions = () => ({
  type: GET_TERMS_AND_CONDITIONS,
  payload: {
    client: 'cost',
    request: {
      method: 'get',
      // TODO complete when we have endpoint for TsandCs
      url: costTermsURL
    }
  }
});

export const getPatientCost = (medcostid: string) => {
  if (!!Config.GET_CARE_SERVICE_EXPANSION_ENABLED) {
    return {
      type: GET_PATIENT_COST,
      payload: {
        client: 'dispatcher',
        request: {
          method: 'get',
          url: getDispatcherUrl(
            DispatcherCategoryService.GET_CARE,
            DispatcherAuthPrefix.AUTHENTICATED,
            DispatcherResource.ESTIMATED_COSTS,
            DispatcherEndpoint.ID
          ),
          params: {
            medcostid
          },
          headers: {
            [DFDHeaders.mhpAdapter]: AdapterSource.MHP,
            [DFDHeaders.resourceId]: medcostid
          }
        }
      }
    };
  }
  return {
    type: GET_PATIENT_COST,
    payload: {
      client: 'cost',
      request: {
        method: 'get',
        url: Config.COST_GET_PATIENT_COST,
        params: {
          medcostid
        }
      }
    }
  };
};

export const getProviders = (
  id: string,
  coords: Coordinate,
  offset?: number,
  pagination?: boolean
) => {
  if (!!Config.GET_CARE_SERVICE_EXPANSION_ENABLED) {
    return {
      type: GET_PROVIDERS,
      payload: {
        client: 'dispatcher',
        request: {
          method: 'get',
          url: getDispatcherUrl(
            DispatcherCategoryService.GET_CARE,
            DispatcherAuthPrefix.AUTHENTICATED,
            DispatcherResource.PROCEDURES,
            DispatcherEndpoint.ID_PROVIDERS
          ),
          params: {
            latitude: coords.latitude,
            longitude: coords.longitude,
            providerServiceGroupId: id,
            distance: 500,
            offset
          },
          headers: {
            [DFDHeaders.mhpAdapter]: AdapterSource.MHP
          }
        }
      }
    };
  }
  return {
    type: GET_PROVIDERS,
    payload: {
      client: 'cost',
      request: {
        method: 'get',
        url: `${Config.COST_URL_GET_PROVIDERS}`,
        params: {
          latitude: coords.latitude,
          longitude: coords.longitude,
          providerservicegroupid: id,
          distance: 500,
          offset
        }
      },
      pagination
    }
  };
};

export const getProviderDetailsForCost = (criteria: types.ProviderDetailCriteria) => ({
  type: PROVIDER_GET_DETAILS_FOR_COST,
  payload: {
    client: 'provider',
    request: {
      url: `${Config.PROVIDER_URL_PROVIDER_DETAILS}/${get(criteria, 'id', '0')}`,
      params: {
        idType: get(criteria, 'idType'),
        getAllProviders: true
      }
    }
  }
});

export const setCostEstimatorSearchText = (value: string) => ({
  type: SET_COST_ESTIMATOR_SEARCH_TEXT,
  payload: value
});

export const GET_PMMC_TOKEN = 'COST.GET_PMMC_TOKEN';
export const GET_PMMC_TOKEN_SUCCESS = 'COST.GET_PMMC_TOKEN_SUCCESS';
export const GET_PMMC_TOKEN_FAIL = 'COST.GET_PMMC_TOKEN_FAIL';

export const getPMMCToken = () => {
  const url = getDispatcherUrl(
    DispatcherCategoryService.GET_CARE,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.PPMCE_PRO,
    DispatcherEndpoint.TOKEN
  );

  return {
    type: GET_PMMC_TOKEN,
    payload: {
      client: 'dispatcher',
      request: {
        headers: { [DFDHeaders.mhpAdapter]: 'mhp' },
        method: 'get',
        url
      }
    }
  };
};
